import { NavLink, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useProject } from "../context/ProjectContext";
import { projectTab } from "../lib/flow";
import {
  Kanban, FolderOpen, Calculator, ShieldCheck, PenNib, FileText, ListBullets,
  Truck, ChartBar, Users, Lightning, Blueprint, UserGear, Books, CurrencyDollar, Compass, Cube,
} from "@phosphor-icons/react";

// Module entries are tabs of the active project — one flow, one project selector.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Kanban, always: true },
  { to: "/projects", label: "Projects", icon: FolderOpen, always: true },
  { tab: "design", label: "Design Engine", icon: Calculator, module: "design_engine" },
  { tab: "validation", label: "Validation", icon: ShieldCheck, module: "validation" },
  { tab: "drawings", label: "Drawing Studio", icon: PenNib, module: "drawing_studio" },
  { tab: "documents", label: "Documents", icon: FileText, module: "document_generation" },
  { tab: "bom", label: "BOM", icon: ListBullets, module: "bom" },
  { to: "/scm", label: "SCM & Vendors", icon: Truck, module: "scm" },
  { to: "/clients", label: "Clients", icon: Users, always: true },
  { to: "/equipment", label: "Equipment Catalog", icon: Books, always: true },
  { to: "/model-engine", label: "Model Engine", icon: Cube, always: true },
  { to: "/symbology", label: "Symbology Library", icon: Compass, always: true },
  { to: "/analytics", label: "Analytics", icon: ChartBar, module: "analytics" },
  { to: "/industry-packs", label: "Industry Packs", icon: Blueprint, always: true, adminOnly: true },
  { to: "/pricing", label: "Plan & Pricing", icon: CurrencyDollar, always: true, adminOnly: true },
  { to: "/admin", label: "Admin", icon: UserGear, always: true, adminOnly: true },
];

const ACCESS_MAP = {
  design_engine: "design", validation: "validation", drawing_studio: "drawings",
  document_generation: "documents", bom: "bom", scm: "scm", analytics: "analytics",
};

export default function Sidebar({ open = true, onClose }) {
  const { user } = useAuth();
  const { pid } = useProject();
  const loc = useLocation();
  const [params] = useSearchParams();
  const activeTab = (params.get("tab") || "overview").toLowerCase();
  const onProject = loc.pathname.startsWith("/projects/") && loc.pathname !== "/projects/new";
  const enabled = user?.org?.enabled_modules || [];
  const mods = user?.modules; // undefined on stale sessions/older backends -> fail open
  const hasMod = (k) => user?.role === "admin" || !mods || mods.includes(k);
  const items = NAV.filter((n) =>
    (n.always || (enabled.includes(n.module) && hasMod(ACCESS_MAP[n.module]))) &&
    (!n.adminOnly || user?.role === "admin"));
  return (
    <aside className={`${open ? "flex" : "hidden"} lg:flex w-60 shrink-0 bg-[var(--c-surface)] border-r border-[var(--c-border)] flex-col max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-40 max-lg:shadow-2xl`} data-testid="sidebar">
      <div className="h-14 flex items-center gap-2.5 px-4 border-b border-[var(--c-border)]">
        <div className="relative w-7 h-7 rounded-md bg-[var(--c-bg)] border border-[var(--c-border)] flex items-center justify-center overflow-hidden">
          <span className="absolute inset-x-0 bottom-0 h-[2px] bg-[var(--c-ember)]" />
          <Lightning size={15} weight="fill" style={{ color: "var(--c-ember)" }} />
        </div>
        <div>
          <div className="text-sm font-semibold tracking-tight text-[var(--c-text)] font-heading">Formigen</div>
          <div className="text-[10px] font-mono tracking-[0.18em] text-[var(--c-muted)] uppercase">Product Design</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="sidebar-nav">
        {items.map((n) => {
          const to = n.tab ? projectTab(pid, n.tab) : n.to;
          const tabActive = !!n.tab && onProject && activeTab === n.tab;
          return (
            <NavLink
              key={n.tab || n.to}
              to={to}
              end={!n.tab && n.to === "/projects"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onClose?.()}
              className={({ isActive }) =>
                `group relative flex items-center gap-2.5 pl-3 pr-3 py-2 rounded-r-md text-sm transition-colors duration-150 ${
                  (n.tab ? tabActive : isActive)
                    ? "bg-[var(--c-surface-2)] text-[var(--c-text)] font-medium"
                    : "text-[var(--c-muted)] hover:text-[var(--c-text)] hover:bg-[var(--c-surface-2)]/60"
                }`
              }
            >
              {({ isActive }) => {
                const act = n.tab ? tabActive : isActive;
                return (
                  <>
                    <span className={`absolute left-0 top-1 bottom-1 w-[2px] rounded-full transition-all duration-200 ${
                      act ? "bg-[var(--c-ember)]" : "bg-transparent group-hover:bg-[var(--c-accent)]/50"}`} />
                    <n.icon size={17} weight={act ? "fill" : "regular"} />
                    {n.label}
                  </>
                );
              }}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-4 border-t border-[var(--c-border)]">
        <div className="text-[10px] font-mono tracking-widest text-[var(--c-muted)] uppercase">Organization</div>
        <div className="text-sm text-[var(--c-text)] truncate mt-1" data-testid="sidebar-org-name">{user?.org?.name || "—"}</div>
        <div className="text-xs text-[var(--c-muted)] mt-0.5">{(user?.org?.industries || []).length} industries · {enabled.length} modules</div>
      </div>
    </aside>
  );
}
